<template>
    <div class="flex flex-col h-[80vh] rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/5 overflow-hidden font-sans">
        <!-- Header -->
        <div class="flex items-center justify-between gap-4 px-5 py-3 border-b border-gray-100">
            <div class="flex items-center gap-3 min-w-0">
                <div class="size-9 rounded-xl bg-gradient-to-br from-primary-100 via-sky-100 to-violet-200 ring-1 ring-inset ring-white/60 flex items-center justify-center shadow-sm shrink-0">
                    <span class="leading-none text-xl">🧙‍♂️</span>
                </div>
                <div class="min-w-0">
                    <div class="flex items-center gap-2">
                        <h3 class="text-sm font-semibold text-gray-900">
                            Albus
                        </h3>
                        <span class="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20">
                            <span class="size-1.5 rounded-full bg-primary-500" />
                            En ligne
                        </span>
                    </div>
                    <p class="text-xs text-gray-500 truncate">
                        Assistant Logiciel pour le Bien-Être et l'Utilisation en Structure
                    </p>
                </div>
            </div>
            <button
                v-if="hasHistory || messages.length"
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                title="Réinitialiser la conversation"
                @click="confirmReset"
            >
                <Icon
                    name="lucide:square-pen"
                    class="size-4"
                />
                <span class="hidden sm:inline">Nouvelle conversation</span>
            </button>
        </div>

        <!-- Messages Area -->
        <div
            id="albus_messages_container"
            ref="messagesContainer"
            class="flex-1 overflow-y-auto bg-gray-50/50"
        >
            <UiLoader v-if="loadingStates.history" />

            <!-- Empty state -->
            <div
                v-else-if="!messages.length && !loadingStates.ask"
                class="relative min-h-full flex flex-col items-center justify-center px-6 py-10 text-center overflow-hidden"
            >
                <div class="pointer-events-none absolute -top-24 -left-16 size-72 rounded-full bg-primary-200/40 blur-3xl" />
                <div class="pointer-events-none absolute -bottom-24 -right-16 size-72 rounded-full bg-violet-200/40 blur-3xl" />

                <div class="relative group/logo mb-5">
                    <div class="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-400 via-sky-500 to-violet-500 blur-xl opacity-40" />
                    <div class="relative size-16 rounded-3xl bg-gradient-to-br from-primary-100 via-sky-100 to-violet-200 ring-1 ring-inset ring-white/60 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover/logo:-rotate-12 group-hover/logo:scale-110">
                        <span class="leading-none text-4xl">🧙‍♂️</span>
                    </div>
                    <Icon
                        name="lucide:sparkles"
                        class="absolute -top-2 -right-3 size-5 text-amber-400 animate-pulse"
                    />
                </div>
                <h2 class="relative text-2xl font-bold text-gray-900">
                    Bonjour ! <span class="inline-block origin-[70%_70%] animate-[albus-wave_2s_ease-in-out_1]">👋</span> Moi c'est
                    <span class="bg-gradient-to-r from-primary-500 via-sky-500 to-violet-500 bg-clip-text text-transparent">Albus</span>
                </h2>
                <p class="relative mt-2 max-w-md text-sm text-gray-500">
                    Une question sur ABCreche ou envie d'explorer les données de votre crèche ? Je suis là pour ça&nbsp;✨
                </p>
                <div class="relative mt-8 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
                    <button
                        v-for="suggestion in suggestions"
                        :key="suggestion.text"
                        type="button"
                        class="group flex items-center gap-3 rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-3 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-gray-300"
                        @click="askSuggestion(suggestion.text)"
                    >
                        <span
                            class="size-10 shrink-0 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-6"
                            :class="suggestion.color"
                        >
                            <Icon
                                :name="suggestion.icon"
                                class="size-5"
                            />
                        </span>
                        <span class="text-sm font-medium text-gray-700">{{ suggestion.text }}</span>
                    </button>
                </div>
            </div>

            <!-- Conversation -->
            <div
                v-else
                class="mx-auto max-w-3xl px-5 py-6 space-y-6"
            >
                <div
                    v-for="message in messages"
                    :key="message.id"
                >
                    <div
                        v-if="message.type === 'user'"
                        class="flex justify-end"
                    >
                        <p class="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-primary-500 to-sky-600 shadow-sm shadow-primary-500/20 px-4 py-2.5 text-sm leading-relaxed text-white whitespace-pre-line">
                            {{ message.content }}
                        </p>
                    </div>
                    <div
                        v-else
                        class="flex gap-3"
                    >
                        <div class="size-7 rounded-lg bg-gradient-to-br from-primary-100 via-sky-100 to-violet-200 ring-1 ring-inset ring-white/60 flex items-center justify-center shrink-0 mt-0.5">
                            <span class="leading-none text-base">🧙‍♂️</span>
                        </div>
                        <div
                            class="min-w-0 flex-1 prose prose-sm prose-gray max-w-none leading-relaxed prose-a:text-primary-700 prose-pre:bg-gray-900 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-table:text-xs"
                            v-html="renderMarkdown(message.content)"
                        />
                    </div>
                </div>

                <!-- Typing indicator -->
                <div
                    v-if="loadingStates.ask"
                    class="flex gap-3"
                >
                    <div class="size-7 rounded-lg bg-gradient-to-br from-primary-100 via-sky-100 to-violet-200 ring-1 ring-inset ring-white/60 flex items-center justify-center shrink-0 mt-0.5">
                        <span class="leading-none text-base animate-pulse">🧙‍♂️</span>
                    </div>
                    <div class="flex items-center gap-3 py-1.5">
                        <div class="flex gap-1">
                            <span
                                class="size-1.5 bg-gray-400 rounded-full animate-bounce"
                                style="animation-delay: -0.32s"
                            />
                            <span
                                class="size-1.5 bg-gray-400 rounded-full animate-bounce"
                                style="animation-delay: -0.16s"
                            />
                            <span class="size-1.5 bg-gray-400 rounded-full animate-bounce" />
                        </div>
                        <span
                            v-if="loadingSentence"
                            class="text-sm text-gray-500"
                        >
                            {{ loadingSentence }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input Area -->
        <div class="border-t border-gray-100 bg-white px-5 pt-4 pb-3">
            <form
                class="mx-auto max-w-3xl flex items-end gap-2 rounded-2xl border border-gray-200 bg-white p-2 pl-4 shadow-sm transition focus-within:border-primary-400 focus-within:ring-4 focus-within:ring-primary-500/10"
                @submit.prevent="sendMessage"
            >
                <textarea
                    id="albus_message_input"
                    ref="messageInput"
                    v-model="currentMessage"
                    placeholder="Demandez-moi n'importe quoi… 🪄"
                    rows="1"
                    class="flex-1 max-h-40 resize-none border-0 bg-transparent p-0 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0"
                    style="height: 36px;"
                    @input="autoResize"
                    @keydown="handleKeydown"
                />
                <button
                    type="submit"
                    :disabled="!currentMessage.trim() || isTyping || loadingStates.ask"
                    class="size-9 shrink-0 rounded-xl bg-gradient-to-br from-primary-500 via-sky-500 to-violet-500 text-white flex items-center justify-center shadow-sm transition hover:scale-105 hover:shadow-md disabled:bg-none disabled:bg-gray-200 disabled:shadow-none disabled:hover:scale-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    title="Envoyer"
                >
                    <Icon
                        name="lucide:arrow-up"
                        class="size-4"
                    />
                </button>
            </form>
            <p class="mx-auto max-w-3xl mt-2 flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
                <Icon
                    name="lucide:shield-check"
                    class="size-3.5"
                />
                Utilisez des numéros de dossier plutôt que des noms d'enfants. Ne partagez aucune information personnelle.
            </p>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import { loadingMixin } from 'abcreche-ui'
import dayjs from 'dayjs'
import MarkdownIt from 'markdown-it'
import { ofetch } from 'ofetch'
import { useAuthStore } from '~~/stores/auth'
import { useRuntimeConfig } from '#app'

// Markdown-It instance (safe config: no raw HTML)
const md = new MarkdownIt({
    html: false,
    linkify: true,
    breaks: true
})
// Ensure links open in new tab safely
const defaultRender = md.renderer.rules.link_open || function (tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options)
}
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
    const aIndex = tokens[idx].attrIndex('target')
    if (aIndex < 0) {
        tokens[idx].attrPush(['target', '_blank'])
    } else {
        tokens[idx].attrs[aIndex][1] = '_blank'
    }
    // rel="noopener noreferrer"
    const relIndex = tokens[idx].attrIndex('rel')
    if (relIndex < 0) {
        tokens[idx].attrPush(['rel', 'noopener noreferrer'])
    } else {
        tokens[idx].attrs[relIndex][1] = 'noopener noreferrer'
    }
    return defaultRender(tokens, idx, options, env, self)
}

export default defineComponent({
    name: 'AlbusChat',
    mixins: [loadingMixin],
    data () {
        return {
            hasHistory: false,
            messages: [],
            currentMessage: '',
            isTyping: false,
            initialTextareaHeight: 36,
            loadingSentence: '',
            chatSessionId: null,
            suggestions: [
                { icon: 'lucide:baby', color: 'bg-pink-100 text-pink-600', text: 'Comment inscrire un nouvel enfant ?' },
                { icon: 'lucide:receipt', color: 'bg-amber-100 text-amber-600', text: 'Comment générer les factures du mois ?' },
                { icon: 'lucide:chart-column', color: 'bg-sky-100 text-sky-600', text: 'Quel est le taux d\'occupation ce mois-ci ?' },
                { icon: 'lucide:calendar-check', color: 'bg-emerald-100 text-emerald-600', text: 'Combien d\'enfants sont présents aujourd\'hui ?' }
            ]
        }
    },
    created () {
        this.loadHistory()
    },
    mounted () {
        this.$nextTick(() => {
            this.resetTextarea()
        })
    },

    methods: {
        isClient () {
            return typeof window !== 'undefined'
        },

        onyxBaseUrl () {
            return 'https://onyx.abcreche.com/api'
        },

        getCrecheUuid () {
            const authStore = useAuthStore()
            return authStore?.currentCreche?.uuid || authStore?.user?.current_creche_uuid || null
        },

        getMcpHeaders () {
            const authStore = useAuthStore()
            const token = authStore.token
            return {
                'Authorization': `Bearer ${token}`,
                'X-Creche-UUID': this.$creche.uuid
                // api_key: token,
                // creche_uuid: this.$creche.uuid
            }
        },

        getAuthHeaders () {
            const config = useRuntimeConfig()
            const token = config?.public?.onyxApiToken
            const crecheUuid = this.getCrecheUuid()

            if (!token) {
                throw new Error('Token Onyx manquant. Veuillez configurer ONYX_API.')
            }
            if (!crecheUuid) {
                throw new Error('Crèche courante introuvable. Veuillez sélectionner une crèche.')
            }

            return {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        },

        onyxClient () {
            return ofetch.create({
                baseURL: this.onyxBaseUrl(),
                retry: 0
            })
        },

        storageKey () {
            const crecheUuid = this.getCrecheUuid() || 'unknown'
            const userUuid = this.$user.uuid
            return `onyx_albus_chat_session_${crecheUuid}_${userUuid}`
        },

        loadSessionFromStorage () {
            if (!this.isClient()) return
            try {
                const sessionId = localStorage.getItem(this.storageKey())
                if (sessionId) {
                    this.chatSessionId = sessionId
                }
            } catch (e) {
                // ignore
            }
        },

        saveSessionToStorage (sessionId) {
            this.chatSessionId = sessionId
            if (!this.isClient()) return
            try {
                localStorage.setItem(this.storageKey(), sessionId)
            } catch (e) {
                // ignore
            }
        },

        clearSessionStorage () {
            if (this.isClient()) {
                try {
                    localStorage.removeItem(this.storageKey())
                } catch (e) {
                    // ignore
                }
            }
            this.chatSessionId = null
        },

        async ensureChatSession () {
            if (this.chatSessionId) {
                return this.chatSessionId
            }

            const api = this.onyxClient()
            const headers = this.getAuthHeaders()

            const response = await api('/chat/create-chat-session', {
                method: 'POST',
                headers,
                body: {
                    persona_id: 1,
                    description: null,
                    project_id: null
                }
            })

            if (!response?.chat_session_id) {
                throw new Error('Impossible de créer une session de chat Onyx.')
            }

            this.saveSessionToStorage(response.chat_session_id)
            return response.chat_session_id
        },

        async fetchChatSession () {
            if (!this.chatSessionId) {
                return null
            }

            const api = this.onyxClient()
            const headers = this.getAuthHeaders()

            return await api(`/chat/get-chat-session/${this.chatSessionId}`, {
                method: 'GET',
                headers
            })
        },

        loadHistory () {
            this.loadSessionFromStorage()

            if (!this.chatSessionId) {
                this.hasHistory = false
                return
            }

            this.load('history')
            this.fetchChatSession()
                .then((session) => {
                    const messages = Array.isArray(session?.messages) ? session.messages : []
                    // Convert API messages to component format
                    this.messages = messages
                        .filter(m => m?.message_type === 'user' || m?.message_type === 'assistant')
                        .map(message => ({
                            id: message.message_id,
                            type: message.message_type,
                            content: message.message,
                            timestamp: message.time_sent ? new Date(message.time_sent) : new Date()
                        }))
                    this.hasHistory = this.messages.length > 0
                    this.done(null, 'history')
                    this.scrollToBottom()
                })
                // .catch((error) => {
                //     this.clearSessionStorage()
                //     this.hasHistory = false
                //     this.doneWithErrors(error, 'history')
                // })
        },
        confirmReset () {
            this.$dialog.show({
                title: 'Voulez-vous réinitialiser la conversation ?',
                message: 'L\'historique sera perdu, mais cela peut être utile pour repartir de zéro en cas de problème avec ALBUS.',
                buttons: [
                    { title: 'Réinitialiser la conversation', class: 'btn-red', handler: this.resetConversation, loadOnClick: true },
                    { title: 'Annuler' }
                ]
            })
        },
        resetConversation () {
            this.load('reset')
            // try {
            Promise.resolve()
                .then(() => {
                    this.clearSessionStorage()
                    // Clear local state
                    this.messages = []
                    this.currentMessage = ''
                    this.resetTextarea()
                    this.hasHistory = false
                    this.$dialog.hide()
                    this.done(null, 'reset')
                })
            // .catch(error => this.doneWithErrors(error, 'reset'))
                .finally(() => {
                    this.scrollToBottom()
                })
            // } catch (error) {
            //     this.doneWithErrors(error, 'reset')
            // }
        },
        async sendMessage () {
            // Add user message
            const userMessage = {
                id: Date.now(),
                type: 'user',
                content: this.currentMessage.trim(),
                timestamp: new Date()
            }
            this.messages.push(userMessage)

            // Clear input
            const messageToSend = this.currentMessage.trim()
            this.currentMessage = ''
            this.autoResize()

            this.scrollToBottom()

            this.load('ask')
            this.loadingSentence = ''

            // Streaming implementation: send message with stream enabled and progressively update assistant reply
            try {
                const sessionId = await this.ensureChatSession()
                const headers = this.getAuthHeaders()

                this.isTyping = true

                // Prepare a placeholder assistant message that will be filled as chunks arrive.
                // It will be pushed into the messages array only when the first delta is received,
                // to avoid showing an empty white bubble.
                const assistantMessage = {
                    id: Date.now() + 1,
                    type: 'assistant',
                    content: '',
                    timestamp: new Date()
                }

                const response = await fetch(`${this.onyxBaseUrl()}/chat/send-chat-message`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        message: messageToSend,
                        chat_session_id: sessionId,
                        parent_message_id: -1,
                        stream: true,
                        mcp_headers: this.getMcpHeaders()
                    })
                })

                if (!response.ok || !response.body) {
                    throw new Error('Erreur lors de la connexion au flux ALBUS.')
                }

                const reader = response.body.getReader()
                const decoder = new TextDecoder('utf-8')

                // Buffer for handling cases where JSON objects are split across chunks
                let done = false
                let buffer = ''
                let hasReceivedDelta = false
                while (!done) {
                    const { value, done: doneReading } = await reader.read()
                    done = doneReading
                    if (value) {
                        const chunk = decoder.decode(value, { stream: !done })
                        buffer += chunk

                        // Split on newlines to get individual JSON objects (JSONL)
                        const lines = buffer.split('\n')
                        // Keep the last partial line (if any) in the buffer
                        buffer = lines.pop() || ''

                        for (const line of lines) {
                            let trimmed = line.trim()
                            if (!trimmed) continue

                            // Handle Server-Sent Events format (e.g. "data: {...}")
                            if (trimmed.startsWith('event:')) {
                                // We don't need to do anything specific with event names for now
                                continue
                            }
                            if (trimmed.startsWith('data:')) {
                                trimmed = trimmed.slice(5).trim()
                                if (!trimmed) continue
                            }

                            // Debug: inspect each stream line after SSE normalization
                            // Remove or comment out in production if too noisy

                            try {
                                const parsed = JSON.parse(trimmed)

                                // We only want to display assistant "message_delta" content
                                if (parsed?.obj?.type === 'message_delta' && typeof parsed.obj.content === 'string') {
                                    if (!hasReceivedDelta) {
                                        // First delta: actually display the assistant bubble
                                        this.messages.push(assistantMessage)
                                        hasReceivedDelta = true
                                        // First visible content: stop the generic loading indicator
                                        if (this.loadingStates.ask) {
                                            this.done(null, 'ask')
                                        }
                                    }
                                    assistantMessage.content += parsed.obj.content
                                    this.scrollToBottom()
                                    // Force re-render in case reactivity doesn't pick nested mutation quickly
                                    this.$forceUpdate()
                                }
                            } catch (e) {
                                // Ignore malformed JSON lines in the stream
                            }
                        }
                    }
                }

                // After streaming, sync with the persisted chat session to ensure IDs/content are aligned
                const session = await this.fetchChatSession()
                const lastAssistant = Array.isArray(session?.messages)
                    ? [...session.messages].reverse().find(m => m?.message_type === 'assistant')
                    : null

                if (lastAssistant?.message) {
                    // Prefer the content we built from the stream; only use server content as a fallback
                    assistantMessage.id = lastAssistant.message_id || assistantMessage.id
                    assistantMessage.timestamp = lastAssistant.time_sent ? new Date(lastAssistant.time_sent) : assistantMessage.timestamp

                    if (!assistantMessage.content) {
                        assistantMessage.content = lastAssistant.message
                    }
                } else if (!assistantMessage.content) {
                    // If nothing was streamed and no assistant message is found, surface an error
                    throw new Error('Réponse ALBUS introuvable.')
                }

                // If, for some reason, no delta was received but we got a final message,
                // ensure we still clear the loading state.
                if (this.loadingStates.ask) {
                    this.done(null, 'ask')
                }

                this.isTyping = false

                this.scrollToBottom()
                this.autoResize()
            } catch (error) {
                this.isTyping = false
                this.doneWithErrors(error, 'ask')
            }
        },
        askSuggestion (text) {
            this.currentMessage = text
            this.sendMessage()
        },
        // Markdown rendering using markdown-it (HTML disabled for safety)
        renderMarkdown (text) {
            return md.render(text || '')
        },

        handleKeydown (event) {
            if (event.key === 'Enter' && !event.shiftKey) {
                // Prevent newline insertion
                event.preventDefault()
                const hasContent = this.currentMessage && this.currentMessage.trim().length > 0
                if (hasContent && !this.isTyping && !this.loadingStates.ask) {
                    this.sendMessage()
                } else {
                    // Ensure height stays at initial when empty
                    this.resetTextarea()
                }
            }
        },

        scrollToBottom () {
            setTimeout(() => {
                const element = document.querySelector('#albus_messages_container')
                element.scrollTo({
                    top: element.scrollHeight,
                    behavior: 'smooth'
                })
            }, 100)
        },

        formatTime (date) {
            const today = dayjs()
            const messageDate = dayjs(date)

            // Check if the message is from today
            const isToday = today.isSame(messageDate, 'day')

            if (isToday) {
                // Show only time for today's messages
                return messageDate.format('HH:mm')
            } else {
                // Show date and time for previous days
                return messageDate.format('DD/MM/YYYY à HH:mm')
            }
        },
        autoResize () {
            // Autogrow the textarea as the user types or inserts new lines
            this.$nextTick(() => {
                const textarea = this.$refs.messageInput
                if (!textarea) return
                textarea.style.height = 'auto'
                const min = this.initialTextareaHeight
                const newHeight = Math.max(textarea.scrollHeight, min)
                textarea.style.height = newHeight + 'px'
            })
        },

        resetTextarea () {
            // Reset the textarea height to its initial state
            const textarea = this.$refs.messageInput
            if (!textarea) return
            textarea.style.height = this.initialTextareaHeight + 'px'
        }
    }
})
</script>

<style>
@keyframes albus-wave {
    0%, 60%, 100% { transform: rotate(0deg); }
    10%, 30% { transform: rotate(14deg); }
    20% { transform: rotate(-8deg); }
    40% { transform: rotate(-4deg); }
    50% { transform: rotate(10deg); }
}
</style>
